import { dayKeyInZone } from '@/lib/dayKey';
import type { CourtState, PublicMatch } from './scheduleStatus';

export interface GridCourt {
  id: string;
  name: string;
  /** Minutes late, from `courtDelayMinutes`; 0 = not late. */
  delayMin: number;
  /** Which venue the column belongs to (drives the venue band); null/absent = none. */
  venueId?: string | null;
}

/** Column id for matches whose court is no longer in the event's court definitions. */
export const OTHER_COURT_ID = '__other__';

/**
 * Columns for the grid: the courts given, plus one trailing "other" column
 * when any match sits on a court that is not defined (an admin removed the
 * court after scheduling). Without it those matches would vanish from the
 * public page; the old flat list still showed them with a blank court.
 */
export function courtsForGrid(
  courts: (Pick<CourtState, 'id' | 'name' | 'delayMin'> & { venueId?: string | null })[],
  matches: PublicMatch[],
  otherLabel: string,
): GridCourt[] {
  const known = new Set(courts.map((c) => c.id));
  const cols: GridCourt[] = courts.map(({ id, name, delayMin, venueId }) => ({
    id,
    name,
    delayMin,
    venueId,
  }));
  if (matches.some((m) => !known.has(m.courtId))) {
    cols.push({ id: OTHER_COURT_ID, name: otherLabel, delayMin: 0 });
  }
  return cols;
}

/** One grid row: every match scheduled to start at `startIso`, one cell per visible court. */
export interface SlotRow {
  startIso: string;
  /** Aligned index-for-index with the court list passed to `buildSlotRows`. */
  cells: PublicMatch[][];
}

/**
 * Group matches into rows by scheduled start and columns by court, so the
 * matches playing concurrently across courts share one row. A match on a
 * court outside `courtIds` lands in the `OTHER_COURT_ID` column when one is
 * present, otherwise it is dropped — the caller decides which courts are
 * visible (all, or the one selected in the court tabs).
 */
export function buildSlotRows(matches: PublicMatch[], courtIds: string[]): SlotRow[] {
  const colByCourt = new Map(courtIds.map((id, i) => [id, i]));
  const otherCol = colByCourt.get(OTHER_COURT_ID);
  const byStart = new Map<string, PublicMatch[][]>();
  for (const m of matches) {
    const col = colByCourt.get(m.courtId) ?? otherCol;
    if (col === undefined) continue;
    let cells = byStart.get(m.scheduledAt);
    if (!cells) {
      cells = courtIds.map(() => []);
      byStart.set(m.scheduledAt, cells);
    }
    cells[col].push(m);
  }
  return [...byStart.entries()]
    .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
    .map(([startIso, cells]) => ({ startIso, cells }));
}

/**
 * Index of the row the NOW divider sits above, or `null` when there is no
 * line to draw. The line belongs to *today only*: `scope.day` is the calendar
 * day the grid is showing and `scope.zone` the IANA zone that day is read in
 * (the same zone the page buckets its days by). On any other day there is no
 * "now" to mark, so the answer is `null`. Rows are compared as instants, so
 * the viewer's own timezone never moves the line. `rows.length` means every
 * row today has already started.
 *
 * Before 2026-10-04 this projected the clock's time-of-day onto whichever day
 * was displayed, so a finished event showed the line on every past day.
 */
export function nowRowIndex(
  rows: { startIso: string }[],
  nowMs: number,
  scope: { day: string; zone: string },
): number | null {
  if (rows.length === 0) return null;
  if (dayKeyInZone(new Date(nowMs).toISOString(), scope.zone) !== scope.day) return null;
  const idx = rows.findIndex((r) => Date.parse(r.startIso) >= nowMs);
  return idx === -1 ? rows.length : idx;
}
